import type { FullProject } from 'sacrifice-utils/api'

type ProjectOverrides = Partial<Pick<FullProject, 'endTime' | 'startTime' | 'inflectionTime'>>

/**
 * Storage for project property overrides.
 * Used for testing/debugging purposes from the browser console.
 */
const projectOverrides = new Map<string, ProjectOverrides>()

/**
 * Event listeners for override changes
 */
const listeners = new Set<() => void>()

/**
 * Subscribe to override changes
 */
export const subscribeToOverrides = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Notify all listeners of changes
 */
const notifyListeners = () => {
  listeners.forEach(listener => listener())
}

/**
 * Set the end time override for a project.
 * @param projectId - The project ID (e.g., 'provex')
 * @param endTime - ISO 8601 date string (e.g., '2026-01-09T00:00:00Z') or null to clear
 */
export const setProjectEndTime = (projectId: string, endTime: string | null): void => {
  const existing = projectOverrides.get(projectId) ?? {}
  if (endTime === null) {
    delete existing.endTime
    if (Object.keys(existing).length === 0) {
      projectOverrides.delete(projectId)
    } else {
      projectOverrides.set(projectId, existing)
    }
  } else {
    projectOverrides.set(projectId, { ...existing, endTime })
  }
  console.log(`✅ Project "${projectId}" endTime ${endTime ? `set to: ${endTime}` : 'cleared'}`)
  notifyListeners()
}

/**
 * Set the start time override for a project.
 * @param projectId - The project ID (e.g., 'provex')
 * @param startTime - ISO 8601 date string or null to clear
 */
export const setProjectStartTime = (projectId: string, startTime: string | null): void => {
  const existing = projectOverrides.get(projectId) ?? {}
  if (startTime === null) {
    delete existing.startTime
    if (Object.keys(existing).length === 0) {
      projectOverrides.delete(projectId)
    } else {
      projectOverrides.set(projectId, existing)
    }
  } else {
    projectOverrides.set(projectId, { ...existing, startTime })
  }
  console.log(
    `✅ Project "${projectId}" startTime ${startTime ? `set to: ${startTime}` : 'cleared'}`
  )
  notifyListeners()
}

/**
 * Set the inflection time override for a project.
 * @param projectId - The project ID (e.g., 'provex')
 * @param inflectionTime - ISO 8601 date string or null to clear
 */
export const setProjectInflectionTime = (
  projectId: string,
  inflectionTime: string | null
): void => {
  const existing = projectOverrides.get(projectId) ?? {}
  if (inflectionTime === null) {
    delete existing.inflectionTime
    if (Object.keys(existing).length === 0) {
      projectOverrides.delete(projectId)
    } else {
      projectOverrides.set(projectId, existing)
    }
  } else {
    projectOverrides.set(projectId, { ...existing, inflectionTime })
  }
  console.log(
    `✅ Project "${projectId}" inflectionTime ${inflectionTime ? `set to: ${inflectionTime}` : 'cleared'}`
  )
  notifyListeners()
}

/**
 * Clear all overrides for a project
 */
export const clearProjectOverrides = (projectId: string): void => {
  projectOverrides.delete(projectId)
  console.log(`✅ All overrides cleared for project "${projectId}"`)
  notifyListeners()
}

/**
 * Clear all project overrides
 */
export const clearAllOverrides = (): void => {
  projectOverrides.clear()
  console.log('✅ All project overrides cleared')
  notifyListeners()
}

/**
 * Get current overrides (for debugging)
 */
export const getOverrides = (): Record<string, ProjectOverrides> => {
  return Object.fromEntries(projectOverrides)
}

/**
 * Apply overrides to a list of projects
 */
export const applyProjectOverrides = (projects: FullProject[]): FullProject[] => {
  if (projectOverrides.size === 0) return projects

  return projects.map(project => {
    const overrides = projectOverrides.get(project.projectId)
    if (!overrides) return project
    return { ...project, ...overrides }
  })
}

/**
 * Expose functions globally for console access
 */
if (typeof window !== 'undefined') {
  const globalFunctions = {
    setProjectEndTime,
    setProjectStartTime,
    setProjectInflectionTime,
    clearProjectOverrides,
    clearAllOverrides,
    getProjectOverrides: getOverrides,
  }

  Object.assign(window, globalFunctions)

  // Log available functions on load
  console.log(
    '%c🔧 Project Override Functions Available:',
    'color: #4CAF50; font-weight: bold; font-size: 12px'
  )
  console.log(`
  setProjectEndTime('projectId', '2026-01-09T00:00:00Z')  - Set end time
  setProjectStartTime('projectId', '2026-01-01T00:00:00Z') - Set start time
  setProjectInflectionTime('projectId', '2026-01-05T00:00:00Z') - Set inflection time
  clearProjectOverrides('projectId') - Clear overrides for a project
  clearAllOverrides() - Clear all overrides
  getProjectOverrides() - View current overrides
  `)
}

// TypeScript declarations for window
declare global {
  interface Window {
    setProjectEndTime: typeof setProjectEndTime
    setProjectStartTime: typeof setProjectStartTime
    setProjectInflectionTime: typeof setProjectInflectionTime
    clearProjectOverrides: typeof clearProjectOverrides
    clearAllOverrides: typeof clearAllOverrides
    getProjectOverrides: typeof getOverrides
  }
}
